import {
  Box,
  CardContent,
  Chip,
  Grid,
  IconButton,
  Link as MuiLink,
  Tooltip,
  Typography,
  capitalize,
} from "@mui/material";
import { grey } from "@mui/material/colors";
import { TemplateStat } from "contexts/Metrics";
import { SubscriptionContext } from "contexts/Subscription";
import { useContext } from "react";
import { UpgradeDialogSource } from "schemas/dashboard";

import {
  Template,
  TemplateContentType,
  TemplateRubricName,
} from "features/Influencer/ContactList/schema";
import { generateColorFromString } from "utils/color";
import { sanitize } from "utils/sanitize";
import { formatEpochTimestamp } from "utils/time";

import {
  calculateScore,
  getLatestRubricByName,
  getScoreColor,
  getScoreLabel,
} from "./helpers";
import styles from "./styles";

interface TemplateCardProps {
  template: Template;
  emailCounts: { [key: number]: number };
  stat?: TemplateStat;
  onMenuOpen: (e: React.MouseEvent<HTMLElement>, template: Template) => void;
}

const formatPercentage = (value?: number) =>
  `${Math.round((value || 0) * 100)}%`;

export default function TemplateCard({
  template,
  emailCounts,
  stat,
  onMenuOpen,
}: TemplateCardProps) {
  const { subscription, setUpgradeDialogSource } =
    useContext(SubscriptionContext);
  const latestRubric = getLatestRubricByName(
    template?.contentType === TemplateContentType.FOLLOWUP
      ? TemplateRubricName.followup_default
      : TemplateRubricName.outreach_default,
    template,
  );

  return (
    <CardContent sx={styles.cardContent}>
      <Box sx={styles.cardHeader}>
        <Box display="flex" flexWrap="wrap" gap={1} sx={{ mb: 1 }}>
          {template?.niches && template?.niches?.length > 0 && (
            <Tooltip
              enterTouchDelay={0}
              title={template?.niches?.map((niche) => niche.name).join(", ")}
            >
              <Box display="flex" flexWrap="wrap">
                {template?.niches && template?.niches?.length > 0 ? (
                  <Chip
                    key={template?.niches?.[0]?.id}
                    label={<>{template?.niches?.[0]?.name}</>}
                    sx={[
                      styles.nichesChip,
                      {
                        zIndex: 2,
                        borderRight: "3px solid white",
                        backgroundColor: generateColorFromString(
                          template?.niches?.[0]?.name,
                          "tagColorPalette",
                        ),
                      },
                    ]}
                  />
                ) : (
                  <></>
                )}
                {template?.niches?.length && template?.niches?.length > 1 ? (
                  <Chip
                    label={<>+{template?.niches?.length - 1}</>}
                    sx={styles.moreChip}
                  />
                ) : (
                  <></>
                )}
              </Box>
            </Tooltip>
          )}

          <Chip
            label={
              <>
                {template?.contentType === TemplateContentType.OUTREACH ? (
                  <Box component="i" className="fa-regular fa-envelope" />
                ) : (
                  <Box component="i" className="fa-regular fa-reply" />
                )}{" "}
                {capitalize(template?.contentType)}
              </>
            }
            sx={styles.contentTypeChip(template?.contentType)}
          />
        </Box>

        <IconButton
          sx={styles.menuButton}
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            onMenuOpen(e, template);
          }}
        >
          <Box
            component="i"
            className="fa-solid fa-ellipsis"
            sx={{ color: grey[300] }}
          />
        </IconButton>
      </Box>

      <Typography sx={styles.templateName}>{template?.name}</Typography>

      <Typography>
        {template?.subject && (
          <Box
            dangerouslySetInnerHTML={{
              __html: `Subject: ${sanitize(template?.subject)}`,
            }}
            sx={{ color: grey[500] }}
          />
        )}
      </Typography>

      {template?.body && (
        <Box
          dangerouslySetInnerHTML={{
            __html:
              template?.body?.length > 400
                ? `${sanitize(template?.body)?.slice(0, 400)}...`
                : sanitize(template?.body),
          }}
          sx={{
            display: "-webkit-box",
            WebkitLineClamp: 6,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
          }}
        />
      )}

      {!latestRubric && (
        <Box
          sx={{
            mt: 1,
            p: 1.25,
            borderRadius: 2,
            border: "1.5px dashed",
            borderColor: "purple.light",
            display: "flex",
            alignItems: "center",
            gap: 1,
            cursor: "pointer",
            "@keyframes pulseGlow": {
              "0%, 100%": { opacity: 0.75, transform: "scale(1)" },
              "50%": { opacity: 1, transform: "scale(1.015)" },
            },
            animation: "pulseGlow 2.4s ease-in-out infinite",
          }}
        >
          <Typography
            sx={{
              fontSize: 12,
              fontWeight: 600,
              color: "primary.main",
              flex: 1,
            }}
          >
            👉 Is this template ready to send? Try our template evaluator tool.
          </Typography>
          <Box
            component="i"
            className="fa-solid fa-chevron-right"
            sx={{ fontSize: 10, color: "purple.light" }}
          />
        </Box>
      )}

      {latestRubric &&
        (() => {
          const score = calculateScore(latestRubric);
          const colors = getScoreColor(score);
          return (
            <Box
              sx={{
                mt: 1,
                p: 1.5,
                borderRadius: 2,
              }}
            >
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                mb={0.75}
              >
                <Typography
                  sx={{ fontSize: 12, fontWeight: 600, color: colors.text }}
                >
                  {getScoreLabel(score)}
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <Typography
                    sx={{ fontSize: 16, fontWeight: 700, color: colors.text }}
                  >
                    {latestRubric?.userScore}
                    <Box
                      component="span"
                      sx={{ color: grey[400], fontSize: 13 }}
                    >
                      / {latestRubric?.totalScore}
                    </Box>
                  </Typography>
                  {score > 0 && (
                    <Tooltip
                      title="Note: This feedback is based on general rules of thumbs and is meant to provide general guidance. If you have a template with a high response rate, you may not need to change a thing!"
                      placement="top"
                      arrow
                    >
                      <Box
                        component="span"
                        className="fa-solid fa-circle-info"
                        sx={{
                          fontSize: 10,
                          color: grey[400],
                          cursor: "default",
                        }}
                      />
                    </Tooltip>
                  )}
                </Box>
              </Box>
              <Box display="flex" gap={0.5}>
                {[0, 1, 2, 3, 4].map((i) => {
                  const fill = Math.min(Math.max(score / 20 - i, 0), 1);
                  return (
                    <Box
                      key={i}
                      sx={{
                        flex: 1,
                        height: 5,
                        borderRadius: 1,
                        backgroundColor: "rgba(0,0,0,0.08)",
                        overflow: "hidden",
                        position: "relative",
                      }}
                    >
                      <Box
                        sx={{
                          position: "absolute",
                          left: 0,
                          top: 0,
                          bottom: 0,
                          width: `${fill * 100}%`,
                          backgroundColor: colors.filled,
                          borderRadius: 1,
                        }}
                      />
                    </Box>
                  );
                })}
              </Box>
            </Box>
          );
        })()}

      {template?.contentType === TemplateContentType.OUTREACH && (
        <Box sx={styles.statsRow}>
          <Box sx={styles.stat}>
            <Typography sx={styles.statValue}>
              {subscription?.isSubscribed ? (
                formatPercentage(stat?.openedPercentage)
              ) : (
                <MuiLink
                  sx={styles.statUpgrade}
                  onClick={(e) => {
                    e.stopPropagation();
                    setUpgradeDialogSource(UpgradeDialogSource.email_tracking);
                  }}
                >
                  Upgrade
                </MuiLink>
              )}
            </Typography>
            <Typography sx={styles.statLabel}>Open rate</Typography>
          </Box>
          <Box sx={styles.stat}>
            <Typography sx={styles.statValue}>
              {formatPercentage(stat?.respondedPercentage)}
            </Typography>
            <Typography sx={styles.statLabel}>Reply rate</Typography>
          </Box>
          <Box sx={styles.stat}>
            <Typography sx={styles.statValue}>
              {(stat?.outreachCount ?? 0).toLocaleString()}
            </Typography>
            <Typography sx={styles.statLabel}>Pitches sent</Typography>
          </Box>
        </Box>
      )}

      <Grid container alignItems="center" gap={1} sx={{ mt: 1 }}>
        {template?.id &&
          template?.id in emailCounts &&
          emailCounts[template?.id] > 0 && (
            <Tooltip
              title={`There are ${emailCounts[template?.id!]} scheduled email(s) using this template.`}
            >
              <Typography sx={styles.lastModified}>
                <Box component="i" className="fa-regular fa-envelope" />{" "}
                {emailCounts[template?.id!]} in use
              </Typography>
            </Tooltip>
          )}
        {template?.updatedAt && (
          <Typography sx={styles.lastModified}>
            Last modified on{" "}
            {template?.updatedAt
              ? formatEpochTimestamp({
                  epochTimestamp: template?.updatedAt,
                  addYear: true,
                })
              : "--"}
          </Typography>
        )}
      </Grid>
    </CardContent>
  );
}
